import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Modal, Segmented, Skeleton, Spin, message } from 'antd';
import { BulbOutlined } from '@ant-design/icons';
import {
  addResumeComment,
  applyStructuredResumeProposal,
  approveResumeVersion,
  bindSearchResume,
  deleteResumeVersion,
  getResumePreviewHtml,
  getResumeVersion,
  dismissStructuredResumeProposal,
  removeResumeSection,
  updateResumeVersion,
} from '../../services/api';
import { captureEvent } from '../../services/analytics';
import {
  getApiErrorMessage,
  isConflictError,
  isNotFoundError,
} from '../../utils/errorUtils';
import { loadSearchDraft, saveSearchDraft } from '../../utils/searchDraft';
import {
  peekPendingResumeBind,
  takePendingResumeBind,
} from '../../utils/pendingResumeBind';
import {
  FixDecision,
  FixProposal,
  ResumeBullet,
  ResumeContent,
  ResumeFixCard,
  ResumeFixRaw,
  ResumeTemplateId,
  ResumeTheme,
  ResumeVersion,
} from '../../types';
import ReadinessMeter from '../../components/tailor/ReadinessMeter';
import TailorFixCard, {
  FixCardOp,
  FixCardStatus,
} from '../../components/tailor/TailorFixCard';
import StructuredProposalCard from '../../components/tailor/StructuredProposalCard';
import { sectionIdForStructuredProposal } from '../../components/tailor/structuredProposalTarget';
import PreviewPane from '../../components/tailor/PreviewPane';
import CommentPopover from '../../components/tailor/CommentPopover';
import EntryEditorModal, {
  EntryEditorDraft,
  EntryEditorTarget,
} from '../../components/tailor/EntryEditorModal';
import ThemeBar from '../../components/tailor/ThemeBar';
import {
  RebuildExplainer,
  RebuildExplainerTitle,
  TemplateEvidence,
  WaitingCoach,
} from './resumeCoaching';
import {
  hasSeenRebuildExplainer,
  markRebuildExplainerSeen,
} from '../../utils/resumeTailorStorage';
import css from './ResumeTailor.module.scss';

// A truthful, coarse label for the one genuinely slow step of draft prep. The
// server stamps fixContext.stage (today only 'reading_resume'); the map keeps
// the label server-driven and falls back to the default when the key is absent
// (older backend) or unknown. Add entries here if the backend emits more stages.
const PREP_STAGE_LABELS: Record<string, string> = {
  reading_resume: 'Reading your resume',
};
const DEFAULT_PREP_STAGE = 'Reading your resume';

// One-time discoverability coachmark: shown on the first editor open, then
// remembered so it never nags again.
const COACHMARK_KEY = 'beast-tailor-preview-coachmark-dismissed';

/** True when the "click any section" coachmark still needs showing. Module
 *  scope so the load effect can call it without taking it as a dependency.
 *  Private-mode storage throws — treat that as already-seen rather than let a
 *  hint break the editor mount. */
const isCoachmarkPending = (): boolean => {
  try {
    return !localStorage.getItem(COACHMARK_KEY);
  } catch (error) {
    console.warn('ResumeTailor: could not read the coachmark flag', error);
    return false;
  }
};

// Async draft preparation (fixContext.status === 'processing'): poll the
// version every 3s until the worker lands a terminal status. 60 polls ≈ 3
// minutes — past that we stop and say so honestly instead of spinning
// forever.
const POLL_INTERVAL_MS = 3_000;
const MAX_POLLS = 60;

/** What the page shows. 'loading' = first GET in flight; 'preparing' =
 *  backend worker still drafting (polling); 'prep-timeout' = poll cap hit
 *  without a terminal status; 'prep-failed' = the worker gave up (the draft
 *  can't be approved); 'error' = a fetch failed; 'ready' = the editor. */
type PagePhase =
  | 'loading'
  | 'preparing'
  | 'prep-timeout'
  | 'prep-failed'
  | 'error'
  | 'ready';

/**
 * One list item on the left pane. Review cards come from fixContext (fixes
 * zipped with surviving proposals by fix_index); comment cards are the
 * locally-appended responses of POST /comments, wrapped in a synthetic raw
 * fix so TailorFixCard renders them with the same chrome.
 */
interface CardVM {
  key: string;
  kind: 'review' | 'comment';
  fix: ResumeFixRaw;
  proposal: FixProposal | null;
  decision: FixDecision | null;
  /** fixContext index for review cards; -1 for comment cards. */
  fixIndex: number;
  /** Where this card's edit would land, read BEFORE mappability may drop the
   *  proposal — the cards that need it most (the unapplicable ones, whose hint
   *  depends on the target) carry `proposal: null`, so it has nowhere else to
   *  come from. Null only for a review fix with no proposal at all. */
  sectionId: string | null;
  /** Async lifecycle — always 'ready' for review cards. */
  status: FixCardStatus;
  /** 'delete' for removal cards; 'replace' otherwise. */
  op: FixCardOp;
  /** User-safe reason on a failed comment card. */
  failureReason: string | null;
}

/**
 * A comment or removal card (fix_index == -1). Kept in LOCAL state, not
 * version.fixContext: their decisions are local-only (the backend fix_decisions
 * log is keyed by fix_index and every one of these is -1, so persisting them
 * would collide under latest-wins). Comment cards start as a `processing`
 * placeholder (POST /comments) and are reconciled BY `id` from the poll path
 * (fixContext.proposals, the source of truth for filled content); remove cards
 * arrive ready from POST /remove-section.
 */
interface CommentCardState {
  key: string;
  /** Poll key — reconcile against fixContext.proposals by this. */
  id: string | null;
  sectionId: string;
  op: FixCardOp;
  status: FixCardStatus;
  before: string | null;
  after: string | null;
  rationale: string | null;
  issue: string | null;
  /** Original note — needed to re-POST on retry. Null for remove cards. */
  note: string | null;
  failureReason: string | null;
  resolved: FixDecision | null;
}

/** "2001" from "2001" / "2001-09"; null when there's no 4-digit year. */
const yearOf = (value?: string | null): string | null =>
  value?.match(/\d{4}/)?.[0] ?? null;

/** The education structural rule (age-signal date drop): a stored `start`/
 *  `end` should be CLEARED when its year no longer appears in the applied
 *  text — the proposal dropped the graduation years. Empty result = the text
 *  keeps every date, so there is nothing structural to apply. */
const educationDatesToClear = (
  entry: ResumeContent['education'][number],
  text: string
): Array<'start' | 'end'> =>
  (['start', 'end'] as const).filter((key) => {
    const year = yearOf(entry[key]);
    return year !== null && !text.includes(year);
  });

/** Whole-section list targets: skills/languages render (and are edited) as
 *  one comma-joined line, extras as one item per line. Splitting the edited
 *  text back mirrors exactly how the server templates join them. */
const LIST_SECTIONS: Record<
  'skills' | 'languages' | 'extras',
  { separator: RegExp; label: string }
> = {
  skills: { separator: /,/, label: 'Skills' },
  languages: { separator: /,/, label: 'Languages' },
  extras: { separator: /\n/, label: 'More' },
};

const isListSectionId = (id: string): id is keyof typeof LIST_SECTIONS =>
  id in LIST_SECTIONS;

const splitListText = (sectionId: keyof typeof LIST_SECTIONS, text: string) =>
  text
    .split(LIST_SECTIONS[sectionId].separator)
    .map((item) => item.trim())
    .filter(Boolean);

/** Write `text` into a deep copy of content at the section the proposal
 *  targets. Summary/bullet ids get a text write; an education entry id gets
 *  the structural date drop above instead (its display text isn't a content
 *  field). `clearAllEducationDates` widens the education drop to EVERY entry
 *  whose years the applied text drops — an age-signal date fix means "old
 *  dates", not "this one entry", and clearing only the target left the other
 *  entry's years advertising the same thing. `applied=false` when there is
 *  nothing honest to change (experience entries, unknown ids, education text
 *  that keeps every date) — callers then record the decision only. */
const applySectionText = (
  content: ResumeContent,
  sectionId: string,
  text: string,
  opts: { clearAllEducationDates?: boolean } = {}
): { content: ResumeContent; applied: boolean } => {
  const copy: ResumeContent = JSON.parse(JSON.stringify(content));
  if (sectionId === '' || sectionId === 'summary') {
    copy.summary = text;
    return { content: copy, applied: true };
  }
  if (isListSectionId(sectionId)) {
    copy[sectionId] = splitListText(sectionId, text);
    return { content: copy, applied: true };
  }
  for (const entry of copy.experience) {
    for (const bullet of entry.bullets) {
      if (bullet.id === sectionId) {
        bullet.text = text;
        return { content: copy, applied: true };
      }
    }
  }
  // A whole-job (experience entry) target is an ADD: append the text as a new
  // bullet with an EMPTY id — the server assigns the real id
  // (assign_new_content_ids_with_uuids), the only sanctioned way to introduce a
  // new content id. Empty text (e.g. a snapshot-less undo fallback) is a no-op,
  // never a blank bullet.
  const expEntry = copy.experience.find((e) => e.id === sectionId);
  if (expEntry) {
    if (!text.trim()) return { content: copy, applied: false };
    expEntry.bullets.push({ id: '', text });
    return { content: copy, applied: true };
  }
  const education = copy.education.find((e) => e.id === sectionId);
  if (education) {
    const targets = opts.clearAllEducationDates ? copy.education : [education];
    let cleared = 0;
    targets.forEach((entry) => {
      educationDatesToClear(entry, text).forEach((key) => {
        entry[key] = null;
        cleared += 1;
      });
    });
    return { content: copy, applied: cleared > 0 };
  }
  return { content: copy, applied: false };
};

const deepClone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

/** Splice the element a delete card targets out of a deep copy of content —
 *  the contract's removal table. Every SURVIVING id is echoed verbatim (only
 *  the removed element's id disappears); section_order is never touched (the
 *  renderer auto-hides an emptied section). `applied=false` for an id that
 *  resolves to nothing (already-removed) so the caller records nothing. */
const applySectionDelete = (
  content: ResumeContent,
  sectionId: string
): { content: ResumeContent; applied: boolean } => {
  const copy: ResumeContent = deepClone(content);
  if (sectionId === '' || sectionId === 'summary') {
    copy.summary = '';
    return { content: copy, applied: true };
  }
  if (sectionId === 'experience') {
    copy.experience = [];
    return { content: copy, applied: true };
  }
  if (sectionId === 'education') {
    copy.education = [];
    return { content: copy, applied: true };
  }
  if (isListSectionId(sectionId)) {
    copy[sectionId] = [];
    return { content: copy, applied: true };
  }
  const expIdx = copy.experience.findIndex((e) => e.id === sectionId);
  if (expIdx !== -1) {
    copy.experience.splice(expIdx, 1);
    return { content: copy, applied: true };
  }
  const eduIdx = copy.education.findIndex((e) => e.id === sectionId);
  if (eduIdx !== -1) {
    copy.education.splice(eduIdx, 1);
    return { content: copy, applied: true };
  }
  for (const entry of copy.experience) {
    const bIdx = entry.bullets.findIndex((b) => b.id === sectionId);
    if (bIdx !== -1) {
      entry.bullets.splice(bIdx, 1);
      return { content: copy, applied: true };
    }
  }
  return { content: copy, applied: false };
};

/** Field-level "did the user leave this alone?", bound to the entry the form was
 *  SEEDED with. Curried so the "is there a baseline at all" question is answered
 *  once rather than per field: with no entry to compare against (an add, or a
 *  target of the other kind) nothing counts as untouched, which is exactly the
 *  behaviour before any of this diffing existed — write what the user typed.
 *
 *  Values are normalized because the form cannot tell null, undefined and ''
 *  apart: it renders all three as an empty box and submits null for one. Without
 *  that, a job stored with no `start` key at all would read as "changed to null"
 *  on every save and clobber a start date someone else had just set. */
const untouchedAgainst =
  <T,>(seeded: T | null) =>
  (
    drafted: string | null | undefined,
    was: string | null | undefined
  ): boolean =>
    seeded !== null && (drafted ?? '') === (was ?? '');

/** Apply one entry-level edit to a deep copy of content. Pure and re-runnable:
 *  a 409 retry re-applies it onto freshly-fetched content, which is what makes
 *  the retry safe (a full-content PATCH of a stale payload would overwrite the
 *  concurrent change). An added entry carries id: '' — the server assigns it.
 *
 *  The draft's `kind` is branched on rather than cast, which is what keeps the
 *  two lists apart: on each arm `draft.entry` is already the element type of
 *  the array it is written into, so filing a school under `experience` does not
 *  compile.
 *
 *  `applied=false` for an EDIT whose id is no longer in the content — a
 *  concurrent write removed the entry — so the caller can say nothing changed
 *  instead of PATCHing the content straight back unchanged. An add always
 *  applies.
 *
 *  It writes ONE THING: the fields the user actually changed. Everything else
 *  on the entry — the id, experience bullets, education `start`, and every form
 *  field they left alone — is taken from the entry AS CURRENTLY STORED.
 *
 *  That is what makes the retry a merge instead of a lost update. `baseline` is
 *  the entry the modal opened on, so comparing the draft against it answers
 *  exactly "did the user type here?", and on the retry arm every field they did
 *  not touch yields to whatever the concurrent writer put there. Without it, Tab
 *  B fixing an end date spreads its whole stale snapshot over Tab A's fresh
 *  company name and reverts it, reporting success — the same lie this whole
 *  feature exists to stop, on the one path built for concurrent writers.
 *
 *  On the first attempt `stored` and `baseline` hold the same values, so both
 *  sides of every branch agree and this changes nothing there.
 *
 *  What a concurrent writer does lose is a field the user typed into as well.
 *  Last write wins on those, which is the edit they came here to make. */
/** Reverse-chronological rank of an entry, as a lexicographically comparable
 *  pair — "YYYY"/"YYYY-MM" strings compare correctly as strings. Experience
 *  ranks by end date first with an open end (Present) newest, then by start:
 *  that is the order every resume in the product reads, and an added entry
 *  must land where a reader expects it, not at the foot of the list (a
 *  finished job appended below a decades-old one reads as "nothing
 *  happened"). Education has no "Present" — a missing year was removed on
 *  purpose (the age-signal fix) or never known, and must not claim the
 *  newest slot, so it ranks oldest. */
const EXPERIENCE_PRESENT_RANK = '9999';
const experienceRank = (
  e: Pick<ResumeContent['experience'][number], 'start' | 'end'>
): readonly [string, string] => [e.end ?? EXPERIENCE_PRESENT_RANK, e.start ?? ''];
const educationRank = (
  e: Pick<ResumeContent['education'][number], 'end'>
): readonly [string, string] => [e.end ?? '', ''];

/** Index where an entry of rank `added` belongs: before the first entry it
 *  outranks, after everything newer (ties keep the older entry first, so the
 *  list stays stable). Works on an unsorted list too — it finds the first
 *  plausible slot rather than assuming global order. */
const insertIndexByRecency = <T,>(
  list: T[],
  rank: (item: T) => readonly [string, string],
  added: readonly [string, string]
): number => {
  const index = list.findIndex((item) => {
    const [end, start] = rank(item);
    return added[0] !== end ? added[0] > end : added[1] > start;
  });
  return index === -1 ? list.length : index;
};

const applyEntryEdit = (
  content: ResumeContent,
  draft: EntryEditorDraft,
  baseline: EntryEditorTarget
): { content: ResumeContent; applied: boolean } => {
  const copy: ResumeContent = deepClone(content);
  if (draft.kind === 'experience') {
    const entry = deepClone(draft.entry);
    if (!entry.id) {
      copy.experience.splice(
        insertIndexByRecency(copy.experience, experienceRank, experienceRank(entry)),
        0,
        entry
      );
      return { content: copy, applied: true };
    }
    const index = copy.experience.findIndex((e) => e.id === entry.id);
    if (index === -1) return { content: copy, applied: false };
    const stored = copy.experience[index];
    const was = baseline.kind === 'experience' ? baseline.entry : null;
    const untouched = untouchedAgainst(was);
    copy.experience[index] = {
      // Stored first: it carries the id and the bullets, neither of which this
      // form may write. Only the fields below can override it.
      ...stored,
      title: untouched(entry.title, was?.title) ? stored.title : entry.title,
      company: untouched(entry.company, was?.company)
        ? stored.company
        : entry.company,
      start: untouched(entry.start, was?.start)
        ? stored.start ?? null
        : entry.start,
      end: untouched(entry.end, was?.end) ? stored.end ?? null : entry.end,
      location: untouched(entry.location, was?.location)
        ? stored.location ?? null
        : entry.location,
    };
    return { content: copy, applied: true };
  }
  const entry = deepClone(draft.entry);
  if (!entry.id) {
    copy.education.splice(
      insertIndexByRecency(copy.education, educationRank, educationRank(entry)),
      0,
      entry
    );
    return { content: copy, applied: true };
  }
  const index = copy.education.findIndex((e) => e.id === entry.id);
  if (index === -1) return { content: copy, applied: false };
  const stored = copy.education[index];
  const was = baseline.kind === 'education' ? baseline.entry : null;
  const untouched = untouchedAgainst(was);
  copy.education[index] = {
    // `start` rides along in the spread — the education twin of bullets, with
    // no control in the form. (`end` IS a field, so it is settled below.)
    ...stored,
    institution: untouched(entry.institution, was?.institution)
      ? stored.institution ?? null
      : entry.institution,
    degree: untouched(entry.degree, was?.degree)
      ? stored.degree ?? null
      : entry.degree,
    end: untouched(entry.end, was?.end) ? stored.end ?? null : entry.end,
  };
  return { content: copy, applied: true };
};

/** Pre-accept snapshot of exactly what applySectionText is about to change,
 *  keyed by card key in page state. Undo restores FROM THE SNAPSHOT instead
 *  of re-deriving the proposal: an applied edit can make its own proposal
 *  unmappable (the education date drop clears the very dates that made it
 *  mappable), and then a proposal-derived undo silently no-ops while the
 *  server keeps the edit. */
type UndoSnapshot =
  | { kind: 'text'; sectionId: string; text: string }
  | { kind: 'list'; sectionId: keyof typeof LIST_SECTIONS; items: string[] }
  | {
      kind: 'education';
      sectionId: string;
      /** ALL entries' dates, not just the target's: an age-signal accept can
       *  clear every entry (clearAllEducationDates), so undo must restore
       *  every entry. Capturing all is harmless for single-entry edits — the
       *  untouched values are written back unchanged. */
      entries: Array<{ id: string; start: string | null; end: string | null }>;
    }
  // Removal (delete-card) undo. Surgical — captures the removed element and
  // where it sat, so restore splices it back WITHOUT disturbing edits made to
  // other sections after the delete (a whole-content revert would clobber them).
  | { kind: 'delete-summary'; sectionId: string; text: string }
  | { kind: 'delete-list'; sectionId: string; items: string[] }
  | {
      kind: 'delete-experience';
      sectionId: string;
      entries: ResumeContent['experience'];
    }
  | {
      kind: 'delete-education';
      sectionId: string;
      entries: ResumeContent['education'];
    }
  | {
      kind: 'delete-exp-entry';
      sectionId: string;
      index: number;
      entry: ResumeContent['experience'][number];
    }
  | {
      kind: 'delete-edu-entry';
      sectionId: string;
      index: number;
      entry: ResumeContent['education'][number];
    }
  | {
      kind: 'delete-bullet';
      sectionId: string;
      entryId: string;
      index: number;
      bullet: ResumeBullet;
    }
  // Add-a-responsibility undo: the added bullet's id is server-assigned, so it
  // is unknown at capture time (null) and filled in post-PATCH. Undo removes
  // exactly that id — surgical, so a second add on the same job survives an undo
  // of the first (a pre-add id-set filter would sweep both).
  | {
      kind: 'add-bullet';
      sectionId: string;
      addedId: string | null;
    };

/** Capture the prior value of the section applySectionText targets. Null for
 *  targets it would not change (mirrors its `applied=false` cases). */
const snapshotSection = (
  content: ResumeContent,
  sectionId: string
): UndoSnapshot | null => {
  if (sectionId === '' || sectionId === 'summary') {
    return { kind: 'text', sectionId, text: content.summary };
  }
  if (isListSectionId(sectionId)) {
    return { kind: 'list', sectionId, items: [...content[sectionId]] };
  }
  for (const entry of content.experience) {
    const bullet = entry.bullets.find((b) => b.id === sectionId);
    if (bullet) return { kind: 'text', sectionId, text: bullet.text };
  }
  // Whole-job target: applySectionText will APPEND a bullet — undo removes it.
  // The appended id is server-assigned, so it is filled in after the PATCH.
  const expEntry = content.experience.find((e) => e.id === sectionId);
  if (expEntry) {
    return { kind: 'add-bullet', sectionId, addedId: null };
  }
  if (content.education.some((e) => e.id === sectionId)) {
    return {
      kind: 'education',
      sectionId,
      entries: content.education.map((e) => ({
        id: e.id,
        start: e.start ?? null,
        end: e.end ?? null,
      })),
    };
  }
  return null;
};

/** Pre-remove snapshot of exactly what applySectionDelete splices out, so undo
 *  can put it back where it was. Mirrors applySectionDelete's cases.
 *
 *  Entry/bullet restores use the position captured at accept time. That is
 *  exact for LIFO undo — the default flow (each removal accepted in turn, then
 *  the "Removed" receipts undone newest-first, top-to-bottom) is LIFO and
 *  reconstructs the original order. Undoing TWO removals ON THE SAME SECTION in
 *  a non-LIFO order can reorder the survivors: once the first element is gone,
 *  the second's snapshot no longer records where the first sat, so no
 *  per-element snapshot can rebuild the original order (that would need a global
 *  op-log). This is the same accepted, point-in-time-snapshot limitation the
 *  text-edit undo carries (see handleUndo), and it is fully recoverable by
 *  re-accepting/re-undoing. */
const snapshotDelete = (
  content: ResumeContent,
  sectionId: string
): UndoSnapshot | null => {
  if (sectionId === '' || sectionId === 'summary') {
    return { kind: 'delete-summary', sectionId, text: content.summary };
  }
  if (sectionId === 'experience') {
    return {
      kind: 'delete-experience',
      sectionId,
      entries: deepClone(content.experience),
    };
  }
  if (sectionId === 'education') {
    return {
      kind: 'delete-education',
      sectionId,
      entries: deepClone(content.education),
    };
  }
  if (isListSectionId(sectionId)) {
    return { kind: 'delete-list', sectionId, items: [...content[sectionId]] };
  }
  const expIdx = content.experience.findIndex((e) => e.id === sectionId);
  if (expIdx !== -1) {
    return {
      kind: 'delete-exp-entry',
      sectionId,
      index: expIdx,
      entry: deepClone(content.experience[expIdx]),
    };
  }
  const eduIdx = content.education.findIndex((e) => e.id === sectionId);
  if (eduIdx !== -1) {
    return {
      kind: 'delete-edu-entry',
      sectionId,
      index: eduIdx,
      entry: deepClone(content.education[eduIdx]),
    };
  }
  for (const entry of content.experience) {
    const bIdx = entry.bullets.findIndex((b) => b.id === sectionId);
    if (bIdx !== -1) {
      return {
        kind: 'delete-bullet',
        sectionId,
        entryId: entry.id,
        index: bIdx,
        bullet: deepClone(entry.bullets[bIdx]),
      };
    }
  }
  return null;
};

/** A restored (previously-removed) element must return WITHOUT its old ids. The
 *  removal dropped those ids server-side, and the id-echo guard (structured
 *  proposals) rejects any content id the server isn't currently storing — so
 *  re-sending the old id 400s the undo. Blanking the id routes the element
 *  through the server's assign-new-id path (the same contract as
 *  add-a-responsibility); surviving siblings keep their ids untouched. */
const idlessBullet = (bullet: ResumeBullet): ResumeBullet => ({
  ...bullet,
  id: '',
});
const idlessExperience = (
  entry: ResumeContent['experience'][number]
): ResumeContent['experience'][number] => ({
  ...entry,
  id: '',
  bullets: entry.bullets.map(idlessBullet),
});
const idlessEducation = (
  entry: ResumeContent['education'][number]
): ResumeContent['education'][number] => ({ ...entry, id: '' });

/** Write a snapshot back into a deep copy of content — the structural inverse
 *  of applySectionText / applySectionDelete. */
const restoreSnapshot = (
  content: ResumeContent,
  snapshot: UndoSnapshot
): { content: ResumeContent; applied: boolean } => {
  if (snapshot.kind === 'text') {
    return applySectionText(content, snapshot.sectionId, snapshot.text);
  }
  if (snapshot.kind === 'list') {
    const copy: ResumeContent = deepClone(content);
    copy[snapshot.sectionId] = [...snapshot.items];
    return { content: copy, applied: true };
  }
  if (snapshot.kind === 'education') {
    const copy: ResumeContent = deepClone(content);
    let applied = false;
    snapshot.entries.forEach((stored) => {
      const education = copy.education.find((e) => e.id === stored.id);
      if (!education) return;
      education.start = stored.start;
      education.end = stored.end;
      applied = true;
    });
    return { content: copy, applied };
  }
  if (snapshot.kind === 'add-bullet') {
    const copy: ResumeContent = deepClone(content);
    const entry = copy.experience.find((e) => e.id === snapshot.sectionId);
    // No addedId (never resolved) → no-op, never guess which bullet to drop.
    if (!entry || !snapshot.addedId) return { content: copy, applied: false };
    const before = entry.bullets.length;
    entry.bullets = entry.bullets.filter((b) => b.id !== snapshot.addedId);
    return { content: copy, applied: entry.bullets.length !== before };
  }
  // Removal undo: put the spliced element back where it was.
  const copy: ResumeContent = deepClone(content);
  switch (snapshot.kind) {
    case 'delete-summary':
      copy.summary = snapshot.text;
      return { content: copy, applied: true };
    case 'delete-list':
      if (isListSectionId(snapshot.sectionId)) {
        copy[snapshot.sectionId] = [...snapshot.items];
      }
      return { content: copy, applied: true };
    case 'delete-experience':
      copy.experience = deepClone(snapshot.entries).map(idlessExperience);
      return { content: copy, applied: true };
    case 'delete-education':
      copy.education = deepClone(snapshot.entries).map(idlessEducation);
      return { content: copy, applied: true };
    case 'delete-exp-entry':
      copy.experience.splice(
        Math.min(snapshot.index, copy.experience.length),
        0,
        idlessExperience(deepClone(snapshot.entry))
      );
      return { content: copy, applied: true };
    case 'delete-edu-entry':
      copy.education.splice(
        Math.min(snapshot.index, copy.education.length),
        0,
        idlessEducation(deepClone(snapshot.entry))
      );
      return { content: copy, applied: true };
    case 'delete-bullet': {
      const entry = copy.experience.find((e) => e.id === snapshot.entryId);
      if (!entry) return { content: copy, applied: false };
      entry.bullets.splice(
        Math.min(snapshot.index, entry.bullets.length),
        0,
        idlessBullet(deepClone(snapshot.bullet))
      );
      return { content: copy, applied: true };
    }
    default:
      return { content: copy, applied: false };
  }
};

/** Would Accept genuinely change the content? Mirrors applySectionText:
 *  summary/'' and bullet ids are text-writable; an education id is mappable
 *  only when accepting `after` would clear at least one date (the age-signal
 *  drop). Everything else — experience entry ids, unknown ids, education
 *  rewrites that keep both dates — is unmappable: the card must degrade to
 *  the fallback (suggestion + Skip + comment routing), not show a fake
 *  Accept. */
const isProposalMappable = (
  content: ResumeContent,
  proposal: FixProposal
): boolean => {
  const id = proposal.section_id;
  if (id === '' || id === 'summary') return true;
  if (isListSectionId(id)) return true;
  if (content.experience.some((e) => e.bullets.some((b) => b.id === id))) {
    return true;
  }
  const education = content.education.find((e) => e.id === id);
  if (education) {
    return educationDatesToClear(education, proposal.after).length > 0;
  }
  return false;
};

/** Mappability for a COMMENT `replace` card — the same rule as
 *  isProposalMappable EXCEPT that an education entry is never mappable.
 *  applySectionText's education branch only CLEARS dates (it exists for the
 *  age-signal review fix), so a free-text note whose draft happens to drop a
 *  year would clear the entry's dates, never write the change the user asked
 *  for, and still report success. Review cards keep the date-drop branch —
 *  dropping the years IS the edit there. */
const isCommentReplaceMappable = (
  content: ResumeContent,
  proposal: FixProposal
): boolean =>
  !content.education.some((e) => e.id === proposal.section_id) &&
  isProposalMappable(content, proposal);

/** Whether a proposal's target is a FREE-TEXT field the user can rewrite — the
 *  only targets applySectionText writes the edited string into: summary, list
 *  sections, a bullet, or an experience entry (append). An education entry is
 *  deliberately excluded: its change is a structural date drop with no text
 *  field, so applySectionText ignores the edited text. Gates the card's Edit
 *  button, so a typed change can never be silently discarded (applying the AI
 *  date-drop instead). Callers only ask for mappable proposals. */
const isProposalTextEditable = (
  content: ResumeContent,
  proposal: FixProposal
): boolean => {
  const id = proposal.section_id;
  if (id === '' || id === 'summary') return true;
  if (isListSectionId(id)) return true;
  if (content.experience.some((e) => e.bullets.some((b) => b.id === id))) {
    return true;
  }
  return content.experience.some((e) => e.id === id);
};

/** Whole-section (heading) targets — now clickable for comment/remove. */
const WHOLE_SECTION_LABELS: Record<string, string> = {
  experience: 'Experience',
  education: 'Education',
};

/** Friendly popover label for a clicked section id. */
const sectionLabelFor = (
  content: ResumeContent | undefined,
  sectionId: string
): string => {
  if (sectionId === '' || sectionId === 'summary') return 'Summary';
  if (sectionId in WHOLE_SECTION_LABELS) return WHOLE_SECTION_LABELS[sectionId];
  if (!content) return 'This section';
  for (const entry of content.experience) {
    if (entry.id === sectionId) return entry.title;
    if (entry.bullets.some((b) => b.id === sectionId)) {
      return `a bullet under ${entry.title}`;
    }
  }
  const edu = content.education.find((e) => e.id === sectionId);
  // Both name fields are nullable server-side (a certification carries no
  // school; a bare degree line carries no degree either), and this label is
  // the popover's title — an empty one asks "What should change in “”?".
  if (edu) return edu.institution || edu.degree || 'This school';
  if (isListSectionId(sectionId)) return LIST_SECTIONS[sectionId].label;
  return sectionId === 'contact' ? 'Contact' : 'This section';
};

/** Precise destructive label for the popover's Remove action — names exactly
 *  what disappears, so "Remove this bullet" never reads as a whole section. */
const removeLabelFor = (
  content: ResumeContent | undefined,
  sectionId: string
): string => {
  if (sectionId === '' || sectionId === 'summary') return 'Remove the summary';
  if (sectionId in WHOLE_SECTION_LABELS) {
    return `Remove the ${WHOLE_SECTION_LABELS[sectionId]} section`;
  }
  if (isListSectionId(sectionId)) {
    return `Remove the ${LIST_SECTIONS[sectionId].label} section`;
  }
  if (content) {
    if (content.experience.some((e) => e.id === sectionId)) {
      return 'Remove this job';
    }
    if (content.education.some((e) => e.id === sectionId)) {
      return 'Remove this school';
    }
    if (
      content.experience.some((e) => e.bullets.some((b) => b.id === sectionId))
    ) {
      return 'Remove this bullet';
    }
  }
  return 'Remove this section';
};

/** What the entry editor opens on for a clicked section: an existing job or
 *  school to edit, a section heading to add under, or null where there is
 *  nothing structural (a bullet, the summary, a list section, the contact
 *  header). The STORED entry is handed over whole — a projection built from the
 *  fields the form shows would drop the ones it doesn't (experience bullets,
 *  education `start`) on the way back out. */
const entryTargetFor = (
  content: ResumeContent,
  sectionId: string
): EntryEditorTarget | null => {
  if (sectionId === 'experience') return { kind: 'experience', entry: null };
  if (sectionId === 'education') return { kind: 'education', entry: null };
  const experience = content.experience.find((e) => e.id === sectionId);
  if (experience) return { kind: 'experience', entry: experience };
  const education = content.education.find((e) => e.id === sectionId);
  if (education) return { kind: 'education', entry: education };
  return null;
};

/** The popover's label for that route. “Edit details” is a CONTRACT, not a
 *  choice: every one of beast-api's four guidance strings (prompts.py, one per
 *  target kind) quotes it verbatim as the thing to look for, so renaming it
 *  here alone would leave the backend's only answer to a structural note
 *  pointing at a button that does not exist. “Add a job” / “Add a school” are
 *  quoted there too. */
/** Which kind of ENTRY a section id names, if any. Derived from the target
 *  above so the two can't drift: one place decides which ids are entries. A
 *  whole-section heading resolves to an ADD target (`entry: null`) — structural,
 *  but not an entry, which is the distinction both callers need. */
const entryKindOf = (
  content: ResumeContent | undefined,
  sectionId: string | null
): 'experience' | 'education' | null => {
  if (!content || !sectionId) return null;
  const target = entryTargetFor(content, sectionId);
  return target?.entry ? target.kind : null;
};

const entryActionLabelFor = (target: EntryEditorTarget): string =>
  target.entry
    ? 'Edit details'
    : target.kind === 'experience'
      ? 'Add a job'
      : 'Add a school';

/** The hint under a REVIEW fallback card, when its target is an entry. The
 *  component default ("click the matching section and tell us what to change")
 *  is a dead end on these two: commenting on a job or a school is answered with
 *  deterministic Skip-only guidance, so following it costs two clicks to learn
 *  nothing. Name the route that works instead. Undefined elsewhere = keep the
 *  default, which is still right for bullets and the summary. */
const ENTRY_EDITOR_HINTS: Record<'experience' | 'education', string> = {
  experience:
    'Dates, title and company are yours to set — click this job in the preview and choose “Edit details”.',
  education:
    'School, degree and year are yours to set — click this school in the preview and choose “Edit details”.',
};

/** The same, for the two section HEADINGS, where the useful routes are both
 *  one click away and neither is a comment. */
const SECTION_EDITOR_HINTS: Record<'experience' | 'education', string> = {
  experience:
    'Click a job in the preview to edit it, or the Experience heading to add one.',
  education:
    'Click a school in the preview to edit it, or the Education heading to add one.',
};

/** How a guidance card identifies itself: beast-api's structural-guidance copy
 *  quotes the button by name. Keying the suppression below on the TEXT rather
 *  than on the target's shape is what makes it survive a deploy window — see
 *  commentFallbackHint. */
const GUIDANCE_PHRASE = '“Edit details”';

const reviewFallbackHint = (
  content: ResumeContent | undefined,
  sectionId: string | null
): string | undefined => {
  const kind = entryKindOf(content, sectionId);
  return kind ? ENTRY_EDITOR_HINTS[kind] : undefined;
};

/** The hint under an unapplicable comment card — null to stay quiet.
 *
 *  Quiet only when the card is ALREADY the server's guidance, which says where
 *  to go; a second paragraph repeating it only says it twice. That is read off
 *  the card's own text (the contract phrase), NOT off the target's shape, and
 *  the difference is a deploy window: with this app live against the previous
 *  API, a note on a job or a heading still comes back as a model-drafted
 *  rewrite that nothing can apply. Suppressing by shape would leave that card
 *  with no Accept, no explanation and no pointer — the exact dead end the
 *  structural route exists to remove. Keying on the text degrades correctly in
 *  both deploy orders.
 *
 *  Otherwise: a structural target gets the route that actually works, and
 *  anything left (an unknown or stale target — a comment still drafting when
 *  the user removed the line it points at) gets the line-level advice. Never
 *  route a comment card back to "click the section in the preview" — that is
 *  what it came from. */
const commentFallbackHint = (
  content: ResumeContent | undefined,
  sectionId: string | null,
  cardText: string
): string | null => {
  if (cardText.includes(GUIDANCE_PHRASE)) return null;
  const entryKind = entryKindOf(content, sectionId);
  if (entryKind) return ENTRY_EDITOR_HINTS[entryKind];
  if (sectionId === 'experience' || sectionId === 'education') {
    return SECTION_EDITOR_HINTS[sectionId];
  }
  return "We can't apply changes to this line directly yet — try commenting on a specific bullet, or the summary, instead.";
};

/** Synthetic raw fix wrapping a comment/remove card so TailorFixCard can render
 *  it. why_it_matters stays empty (renders as a blank line, no chrome). While a
 *  comment is still processing/failed it has no drafted issue yet, so the user's
 *  own note stands in as the heading. */
const syntheticFixFor = (c: CommentCardState): ResumeFixRaw => {
  const quotedNote = c.note?.trim() ? `“${c.note.trim()}”` : null;
  const issue =
    c.op === 'delete'
      ? c.issue ?? 'Remove this section'
      : c.status === 'ready'
        ? c.issue ?? 'Your note'
        : quotedNote ?? 'Your note';
  return {
    category: 'completeness',
    issue,
    why_it_matters: '',
    suggestion: c.after ?? '',
    priority: 'medium',
  };
};

/** The concrete edit a READY comment/remove card carries. Nulls coalesce to
 *  '' — a ready card always has real content, this only satisfies the types. */
const proposalFromCard = (c: CommentCardState): FixProposal => ({
  fix_index: -1,
  section_id: c.sectionId,
  before: c.before ?? '',
  after: c.after ?? '',
  rationale: c.rationale ?? '',
  issue: c.issue ?? undefined,
});

/** Seed a local card from a POST response (placeholder or ready). `note` is the
 *  user's original text, kept for retry (null for remove cards). */
const seedCommentCard = (
  card: ResumeFixCard,
  key: string,
  note: string | null
): CommentCardState => ({
  key,
  id: card.id ?? null,
  sectionId: card.sectionId,
  op: card.op ?? 'replace',
  status: card.status ?? 'ready', // legacy synchronous response => ready
  before: card.before,
  after: card.after,
  rationale: card.rationale,
  issue: card.issue ?? null,
  note,
  failureReason: card.failureReason ?? null,
  resolved: null,
});

// Fold a freshly-read CAS revision into the editor state, monotonically. Every
// server write to fixContext advances `content_revision` — the token Accept /
// PATCH / apply must echo — but the interactive card writes (POST /comments,
// its async fill, POST /remove-section) return only the card, never the new
// revision. Without folding it back, the editor keeps the pre-card revision and
// the next Accept 409s (mirrors the batch-proposal sync in the polling effect).
// `Math.max` never regresses behind a higher revision a concurrent Accept
// response may have already set.
const withContentRevision =
  (loaded: Pick<ResumeVersion, 'contentRevision'>) =>
  (previous: ResumeVersion | null): ResumeVersion | null =>
    previous
      ? {
          ...previous,
          contentRevision: Math.max(
            loaded.contentRevision ?? 0,
            previous.contentRevision ?? 0
          ),
        }
      : previous;

const ResumeTailor: React.FC = () => {
  const { versionId } = useParams<{ versionId: string }>();
  const navigate = useNavigate();

  // How you reached the editor decides where "Back" goes. Edit/Tailor from a
  // search card stash a pendingResumeBind for this version, so backing out
  // returns to the searches LIST; the create-search wizard leaves none, so it
  // returns to the setup drawer. Peek (never consume) so a later approve can
  // still bind, and freeze at mount so approve consuming the bind can't flip it.
  const [enteredFromSearch] = useState(
    () => !!versionId && peekPendingResumeBind(versionId) !== null
  );
  const backLabel = enteredFromSearch
    ? '← Back to searches'
    : '← Back to search setup';

  const [version, setVersion] = useState<ResumeVersion | null>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [phase, setPhase] = useState<PagePhase>('loading');
  const [prepFailureReason, setPrepFailureReason] = useState<string | null>(
    null
  );
  // A locally recreated development database can leave a browser on a route
  // for a draft that no longer exists. Keep that distinct from a transient
  // load failure so the recovery action is clear.
  const [versionNotFound, setVersionNotFound] = useState(false);
  // Bumped by Retry on the timeout screen: re-runs the load effect, which
  // refetches immediately and starts a FRESH poll budget.
  const [pollEpoch, setPollEpoch] = useState(0);

  const [commentCards, setCommentCards] = useState<CommentCardState[]>([]);
  const commentSeq = useRef(0);

  // LOCAL-ONLY undo: the backend decision log is append-only with no
  // 'undecided' entry, so undo overrides the persisted decision here and the
  // card renders active again; a later Accept/Skip appends a decision that
  // wins (and clears the override).
  const [undoneFixes, setUndoneFixes] = useState<Set<number>>(new Set());

  // Pre-accept snapshots (per card key) of what the accept changed, so undo
  // restores the real prior value — see UndoSnapshot. Session-local: undo of
  // a decision persisted before this page load still falls back to
  // proposal.before.
  const [undoSnapshots, setUndoSnapshots] = useState<
    Record<string, UndoSnapshot>
  >({});

  /** Key of the card with an in-flight mutation; gates ALL cards (matches
   *  TailorFixCard's busy contract). */
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  // True once the user has changed the draft's content or appearance (accept /
  // edit / remove / add / apply a structured proposal / theme). The editor
  // works on a THROWAWAY fork — only approve binds a version to a search — so
  // this gates the confirm-on-leave: a pristine draft just leaves, a dirtied one
  // asks before discarding. Skips don't set it (nothing to lose).
  const [dirty, setDirty] = useState(false);

  const [highlightSectionId, setHighlightSectionId] = useState<string | null>(
    null
  );
  const [popover, setPopover] = useState<{
    sectionId: string;
    label: string;
  } | null>(null);
  const [commentBusy, setCommentBusy] = useState(false);
  const [removeBusy, setRemoveBusy] = useState(false);
  // What the entry editor is open on. Null closes it BY UNMOUNTING: its
  // `target` prop is non-nullable, so there is nothing to keep it mounted with
  // while closed, and unmounting also moots an edge in the modal's seed key —
  // an add-target and an entry whose id is still '' key the same, so a form
  // that survived the close could carry one's values into the other.
  const [entryEditor, setEntryEditor] = useState<EntryEditorTarget | null>(
    null
  );
  // Owned here, not folded into busyKey: the editor is a modal, so no card is
  // clickable while it saves. It gates the modal's Cancel, close X, Esc, mask
  // and submit.
  const [entrySaving, setEntrySaving] = useState(false);
  // Key of a just-created card to flash + scroll into view; cleared after the
  // animation so it fires once.
  const [flashCardKey, setFlashCardKey] = useState<string | null>(null);
  // Glow the element the user just tapped in the preview (mirrors the accept
  // glow). The nonce forces a re-fire when the same section is tapped twice.
  const [previewPulse, setPreviewPulse] = useState<{
    sectionId: string;
    nonce: number;
  } | null>(null);
  const pulseNonce = useRef(0);
  // Undo for the LAST applied structured proposal: a full pre-apply content
  // snapshot, valid only while the apply is still the newest content mutation
  // (revisionAfterApply === current revision). Any later edit retires the
  // offer — a full-content restore must never clobber a newer change. Session-
  // local, like the legacy undo snapshots.
  const [structuredUndo, setStructuredUndo] = useState<{
    proposalId: string;
    content: ResumeContent;
    revisionAfterApply: number;
  } | null>(null);
  // Coarse server-reported preparation stage, shown on the waiting screen.
  const [prepStage, setPrepStage] = useState<string | null>(null);
  // "Why this layout?" evidence modal.
  const [whyOpen, setWhyOpen] = useState(false);
  // First-run only: "why your resume looks different". Runs AHEAD of the
  // coachmark — see handleExplainerDismiss.
  const [explainerOpen, setExplainerOpen] = useState(false);
  // One-time preview coachmark — hidden if the user has dismissed it before.
  const [coachmarkOpen, setCoachmarkOpen] = useState(false);

  const [mobilePane, setMobilePane] = useState<'fixes' | 'preview'>('fixes');
  const [showSeeIt, setShowSeeIt] = useState(false);

  // Load flow: fetch the version FIRST and branch on fixContext.status.
  // While the backend worker is still preparing the draft ('processing') the
  // content is a PLACEHOLDER — never fetch/render its preview; poll instead.
  // Missing status = legacy synchronous draft = ready.
  useEffect(() => {
    if (!versionId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    setVersionNotFound(false);

    const showEditor = async (loaded: ResumeVersion) => {
      const html = await getResumePreviewHtml(versionId);
      if (cancelled) return;
      setVersion(loaded);
      setPreviewHtml(html);
      setPhase('ready');
      // Trust before mechanics. On the very first rebuilt resume, answer "what
      // did you do to my resume?" — the "sections are clickable" hint waits for
      // that modal's dismissal so the two never compete for the same glance.
      if (!hasSeenRebuildExplainer()) {
        setExplainerOpen(true);
        captureEvent('resume_rebuild_explainer_shown', { surface: 'editor' });
      } else if (isCoachmarkPending()) {
        setCoachmarkOpen(true);
      }
      captureEvent('resume_tailor_opened', {
        fixCount: loaded.fixContext?.fixes.length ?? 0,
      });
    };

    /** Route a fetched version to its terminal state. True = settled (editor
     *  or failed), false = still processing — keep polling. */
    const settle = async (loaded: ResumeVersion): Promise<boolean> => {
      const status = loaded.fixContext?.status;
      if (status === 'failed') {
        setPrepFailureReason(loaded.fixContext?.failure_reason ?? null);
        setPhase('prep-failed');
        return true;
      }
      if (status === 'processing') {
        setPrepStage(loaded.fixContext?.stage ?? null);
        return false;
      }
      await showEditor(loaded);
      return true;
    };

    const poll = (remaining: number) => {
      timer = setTimeout(async () => {
        try {
          const loaded = await getResumeVersion(versionId);
          if (cancelled) return;
          if (await settle(loaded)) return;
          if (remaining <= 1) {
            setPhase('prep-timeout');
            return;
          }
          poll(remaining - 1);
        } catch (error) {
          if (cancelled) return;
          setVersionNotFound(isNotFoundError(error));
          setPhase('error');
          message.error(getApiErrorMessage(error));
        }
      }, POLL_INTERVAL_MS);
    };

    (async () => {
      try {
        const loaded = await getResumeVersion(versionId);
        if (cancelled) return;
        if (await settle(loaded)) return;
        setPhase('preparing');
        poll(MAX_POLLS);
      } catch (error) {
        if (cancelled) return;
        setVersionNotFound(isNotFoundError(error));
        setPhase('error');
        message.error(getApiErrorMessage(error));
      }
    })();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [versionId, pollEpoch]);

  // Batch proposal generation is deliberately non-blocking: once the editor
  // opens, keep its local decisions/content intact and refresh only the
  // server-owned proposal list until the optional worker finishes.
  const batchProposalStatus = version?.fixContext?.proposals_status;
  useEffect(() => {
    if (!versionId || batchProposalStatus !== 'processing') return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = () => {
      timer = setTimeout(async () => {
        try {
          const loaded = await getResumeVersion(versionId);
          if (cancelled) return;
          setVersion((previous) => {
            if (!previous?.fixContext) return previous;
            return {
              ...previous,
              // Proposal generation is a server-owned write that advances the
              // same CAS revision Apply uses. Keep it in sync with the cards;
              // otherwise the first visible Apply sends the pre-generation
              // revision and always receives a 409.
              contentRevision: loaded.contentRevision ?? previous.contentRevision,
              fixContext: {
                ...previous.fixContext,
                proposals: loaded.fixContext?.proposals ?? previous.fixContext.proposals,
                proposals_status:
                  loaded.fixContext?.proposals_status ?? previous.fixContext.proposals_status,
              },
            };
          });
          if (loaded.fixContext?.proposals_status === 'processing') poll();
        } catch {
          // Proposal cards are optional; leave the review suggestions usable.
        }
      }, POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [versionId, batchProposalStatus]);

  // Readiness label seed: the version doesn't carry the review verdict — the
  // search draft that routed the user here does. Fall back to needs_work
  // (there are fixes to work through by definition of landing here).
  const readiness = useMemo(() => {
    try {
      return loadSearchDraft()?.review?.readiness ?? 'needs_work';
    } catch (error) {
      // Swallowed on purpose: a corrupt/unreadable draft must not block the
      // editor — the label seed just falls back. Warn so it isn't invisible.
      console.warn('Failed to read search draft for readiness seed', error);
      return 'needs_work';
    }
  }, []);

  const fixContext = version?.fixContext ?? null;

  const content = version?.content;

  const structuredProposals = useMemo(
    () => (fixContext?.proposals ?? []).filter(
      (proposal) => Boolean(proposal.proposal_id && proposal.patches)
    ),
    [fixContext]
  );

  const cards = useMemo<CardVM[]>(() => {
    if (!fixContext || !content) return [];
    // Unmappable section targets (see isProposalMappable) surface WITHOUT
    // their proposal so TailorFixCard renders the honest fallback — a fake
    // Accept that silently changes nothing is worse than no Accept.
    const mappedProposal = (proposal: FixProposal | null) =>
      proposal && isProposalMappable(content, proposal) ? proposal : null;
    // Latest persisted decision per fix_index wins (append-only log).
    const latest = new Map<number, FixDecision>();
    fixContext.decisions.forEach((d) => latest.set(d.fix_index, d));
    const structuredFixes = new Set(
      structuredProposals
        .filter((proposal) => proposal.status === 'ready' || proposal.status === 'processing')
        .map((proposal) => proposal.fix_index)
    );
    const reviewCards = fixContext.fixes.flatMap((fix, i): CardVM[] => {
      // A structured proposal has its own full diff card. Do not also render
      // the old replacement-only/fallback card for the same review fix.
      if (structuredFixes.has(i)) return [];
      const raw =
        fixContext.proposals.find((p) => p.fix_index === i && !p.proposal_id) ?? null;
      const proposal = mappedProposal(raw);
      const decision = undoneFixes.has(i) ? null : latest.get(i) ?? null;
      return [
        {
          key: `fix-${i}`,
          kind: 'review',
          fix,
          proposal,
          decision,
          fixIndex: i,
          sectionId: raw?.section_id ?? null,
          status: 'ready',
          op: 'replace',
          failureReason: null,
        },
      ];
    });
    const comments = commentCards.map((c): CardVM => {
      const fix = syntheticFixFor(c);
      // Processing/failed cards have nothing to act on yet — no proposal.
      if (c.status !== 'ready') {
        return {
          key: c.key,
          kind: 'comment',
          fix,
          proposal: null,
          decision: c.resolved,
          fixIndex: -1,
          sectionId: c.sectionId,
          status: c.status,
          op: c.op,
          failureReason: c.failureReason,
        };
      }
      const raw = proposalFromCard(c);
      // A delete card is always applicable via the splice table, so it bypasses
      // the mappability filter outright. An ADD bypasses it only for the target
      // it is defined on — a whole JOB, where applySectionText appends a bullet.
      // That dispatch is by id SHAPE, not by op: an add carrying an education
      // id would fall into the date-clearing branch, and one carrying '' would
      // overwrite the summary. The backend only ever pins 'add' on a job, but
      // that is a cross-repo invariant and stored cards predate it, so check the
      // shape here. A replace goes through the comment gate — the review rule
      // minus the education date drop (see isCommentReplaceMappable).
      const proposal =
        c.op === 'delete'
          ? raw
          : c.op === 'add'
            ? content.experience.some((e) => e.id === raw.section_id)
              ? raw
              : null
            : isCommentReplaceMappable(content, raw)
              ? raw
              : null;
      return {
        key: c.key,
        kind: 'comment',
        fix,
        proposal,
        decision: c.resolved,
        fixIndex: -1,
        sectionId: c.sectionId,
        status: 'ready',
        op: c.op,
        failureReason: null,
      };
    });
    // New comment/remove cards land on top of the list.
    return [...comments, ...reviewCards];
  }, [fixContext, content, commentCards, undoneFixes, structuredProposals]);

  // Async comments: POST /comments returns a `processing` placeholder; the
  // worker fills it in. Poll GET /versions/{id} while any comment card is still
  // processing and reconcile each BY id from the poll path (fixContext.proposals
  // — the source of truth for filled content). Decoupled from `version` state
  // (read-only) so it never races a content/decision PATCH. Same 3-min client
  // cap as draft-prep; the server self-heals stuck cards to `failed` at 15 min.
  const processingKey = commentCards
    .filter((c) => c.status === 'processing' && c.id)
    .map((c) => c.id)
    .sort()
    .join(',');
  useEffect(() => {
    if (!versionId || !processingKey) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const reconcile = (loaded: ResumeVersion) => {
      const proposals = loaded.fixContext?.proposals ?? [];
      setCommentCards((prev) =>
        prev.map((card) => {
          if (card.status !== 'processing' || !card.id) return card;
          const p = proposals.find((pp) => pp.id === card.id);
          if (!p) return card; // not landed in the poll yet
          const status = p.status ?? 'ready';
          if (status === 'processing') return card;
          if (status === 'failed') {
            return {
              ...card,
              status: 'failed',
              failureReason: p.failure_reason ?? null,
            };
          }
          return {
            ...card,
            status: 'ready',
            // op is re-pinned by the worker (a whole-job note fills as 'add');
            // without carrying it the card would stay 'replace' and never apply.
            op: p.op ?? card.op,
            before: p.before ?? null,
            after: p.after ?? null,
            rationale: p.rationale ?? null,
            issue: p.issue ?? card.issue,
          };
        })
      );
    };

    // Client cap hit with cards still drafting: surface them as failed +
    // retry rather than spinning forever (mirrors the draft-prep timeout).
    const timeOutStuck = () => {
      setCommentCards((prev) =>
        prev.map((card) =>
          card.status === 'processing'
            ? {
                ...card,
                status: 'failed',
                failureReason: 'this is taking longer than usual',
              }
            : card
        )
      );
    };

    const poll = (remaining: number) => {
      timer = setTimeout(async () => {
        try {
          const loaded = await getResumeVersion(versionId);
          if (cancelled) return;
          reconcile(loaded);
          // Comment create + async fill each advanced the shared CAS revision
          // that Accept/PATCH echoes. This poll owns the only client view of
          // those bumps (it is otherwise decoupled from `version`), so fold the
          // latest revision back in — else the first Accept on the filled card
          // (or any other ready card) sends the pre-comment revision and 409s.
          setVersion(withContentRevision(loaded));
        } catch {
          // Transient GET failure — keep polling within the budget rather than
          // failing the card; a stuck card times out below.
        }
        if (cancelled) return;
        if (remaining <= 1) {
          timeOutStuck();
          return;
        }
        poll(remaining - 1);
      }, POLL_INTERVAL_MS);
    };

    poll(MAX_POLLS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // processingKey changes when the set of in-flight cards changes, restarting
    // the loop with a fresh budget so a newly added comment gets a full window.
  }, [versionId, processingKey]);

  // The flash is a one-shot: drop the key once its animation (cardFlash, 1.9s in
  // TailorFixCard.module.scss) is done so re-creating a card in the same slot
  // re-triggers it. Kept in sync with that keyframe's duration.
  useEffect(() => {
    if (!flashCardKey) return;
    const t = setTimeout(() => setFlashCardKey(null), 1900);
    return () => clearTimeout(t);
  }, [flashCardKey]);

  const totalFixes = cards.length + structuredProposals.filter(
    (proposal) => !['applied', 'dismissed', 'rejected'].includes(proposal.status ?? 'ready')
  ).length;
  const resolvedCount = cards.filter((c) => c.decision !== null).length;

  const refetchPreview = async (id: string, highlight: string | null) => {
    const html = await getResumePreviewHtml(id);
    // Both set in the same async continuation → one render: the pane arms the
    // highlight and reloads the document together.
    setHighlightSectionId(highlight);
    setPreviewHtml(html);
  };

  const handleAccept = async (card: CardVM, text: string) => {
    if (!version || !card.proposal || busyKey) return;
    setBusyKey(card.key);
    // Drop any pending highlight first so consecutive accepts on the SAME
    // section still register a prop change when re-armed below.
    setHighlightSectionId(null);
    try {
      const sectionId = card.proposal.section_id;
      const isDelete = card.op === 'delete';
      // Delete cards splice the element out; replace cards write text in.
      const { content, applied } = isDelete
        ? applySectionDelete(version.content, sectionId)
        : applySectionText(
            version.content,
            sectionId,
            text,
            // An age-signal education drop means "old dates", not "this entry":
            // clear the dates the applied text drops on EVERY education entry.
            { clearAllEducationDates: card.fix.category === 'age_signals' }
          );
      const decision: FixDecision['decision'] = isDelete
        ? 'accepted'
        : text === card.proposal.after
          ? 'accepted'
          : 'edited';
      // Captured BEFORE the PATCH lands: the prior value undo must restore. For
      // an add the specific appended id is only known post-PATCH, so the
      // add-bullet snapshot is finalized in the comment branch below.
      let snapshot = applied
        ? isDelete
          ? snapshotDelete(version.content, sectionId)
          : snapshotSection(version.content, sectionId)
        : null;
      // What to glow once the preview reloads: the edited section, or — for an
      // add — the freshly appended bullet (its id is only known post-PATCH,
      // filled in below). A removal has nothing left to glow.
      let highlightSection: string | null = isDelete ? null : sectionId;

      if (card.kind === 'review') {
        if (!applied) {
          // Rare by construction: unmappable targets render as fallback cards
          // with no Accept. Reachable only when an EDIT re-adds both years to
          // an education date-drop (nothing left to clear) — record the
          // decision only so the audit trail still reflects the user's call.
          console.warn(
            `ResumeTailor: no editable text target for section "${sectionId}" — recording decision only`
          );
        }
        const fixDecisions = [
          ...(version.fixContext?.decisions ?? []),
          { fix_index: card.fixIndex, decision, applied_text: text },
        ];
        const updated = await updateResumeVersion(
          version.id,
          {
            ...(applied ? { content, fixDecisions } : { fixDecisions }),
            expectedContentRevision: version.contentRevision ?? 0,
          }
        );
        setVersion(updated);
        setUndoneFixes((prev) => {
          if (!prev.has(card.fixIndex)) return prev;
          const next = new Set(prev);
          next.delete(card.fixIndex);
          return next;
        });
      } else {
        // Comment card: content-only PATCH; the resolution lives in local
        // state (see CommentCardState.resolved for why not fix_decisions).
        if (applied) {
          const updated = await updateResumeVersion(version.id, {
            content, expectedContentRevision: version.contentRevision ?? 0,
          });
          setVersion(updated);
          if (card.op === 'add') {
            // The appended bullet's id was assigned by the server — find the id
            // now present under this job that wasn't before. Glow THAT line
            // (|| not ??, so an unexpected empty id falls back to the job block),
            // and pin it into the undo snapshot so undo drops exactly this line.
            const priorIds = new Set(
              (version.content.experience.find((e) => e.id === sectionId)
                ?.bullets ?? []).map((b) => b.id)
            );
            const addedId =
              updated.content.experience
                .find((e) => e.id === sectionId)
                ?.bullets.find((b) => !priorIds.has(b.id))?.id || null;
            highlightSection = addedId || sectionId;
            if (snapshot && snapshot.kind === 'add-bullet') {
              snapshot = { ...snapshot, addedId };
            }
          }
        } else {
          // Nothing was written, so nothing may be recorded: resolving here
          // would show "Accepted" over an unchanged resume — the product
          // telling the user it did something it did not do. Bail before the
          // resolution, the undo snapshot, the dirty flag, the accepted
          // analytics event and the preview refetch; the `finally` below still
          // clears busyKey, so every other card stays live.
          //
          // ONE route reaches this from the UI: a DELETE card — which bypasses
          // the mappability gate outright, by design, since the splice table
          // handles any id — whose target another card has already spliced out.
          // Remove a bullet, remove its whole job, accept the bullet card.
          //
          // The 'add' route is NOT reachable: it would need an empty `after`,
          // and beast-api's CommentProposalModel rejects that at PARSE time for
          // op in {replace, add} (`after must be non-empty when op is …`), a
          // validator that predates the add op. The add case is still covered
          // in the tests, as defence against that cross-repo contract being
          // relaxed — this file does not trust op/target invariants it cannot
          // see — but it is not a live bug.
          //
          // The copy names a DIFFERENT action on purpose. handleAccept reads
          // `version.content`, which cannot change without another PATCH, so
          // for a given card and content state this fails deterministically,
          // forever: "try again" would be advice we know does not work.
          console.error(
            `ResumeTailor: no editable target for section "${sectionId}" — the card was NOT applied`
          );
          // This branch deliberately records nothing else, which would leave a
          // user-visible failure invisible in both Sentry and PostHog — and no
          // way to learn whether the orphaned-delete route fires once a year or
          // once an hour. Not an analytics-for-a-success: it reports the miss.
          captureEvent('resume_fix_apply_failed', { sectionId, op: card.op });
          message.error(
            'Nothing changed — skip this one and ask again if you still need it.'
          );
          return;
        }
        setCommentCards((prev) =>
          prev.map((c) =>
            c.key === card.key
              ? {
                  ...c,
                  resolved: {
                    fix_index: -1,
                    decision,
                    applied_text: isDelete ? undefined : text,
                  },
                }
              : c
          )
        );
      }

      // Capture into a const: `snapshot` is a `let` (finalized for adds above),
      // and TS won't narrow a reassignable binding inside the setter closure.
      const finalSnapshot = snapshot;
      if (finalSnapshot) {
        setUndoSnapshots((prev) => ({ ...prev, [card.key]: finalSnapshot }));
      }

      setDirty(true);
      captureEvent(isDelete ? 'resume_section_removed' : 'resume_fix_accepted', {
        fixIndex: card.fixIndex,
        ...(isDelete ? { sectionId } : { decision }),
      });
      if (applied) {
        // A removed section is gone — nothing to glow; a replace glows its
        // edit; an add glows the new bullet (highlightSection set above).
        await refetchPreview(version.id, highlightSection);
        setShowSeeIt(true);
      }
    } catch (error) {
      message.error(getApiErrorMessage(error));
    } finally {
      setBusyKey(null);
    }
  };

  const handleStructuredApply = async (proposal: FixProposal) => {
    if (!version || !proposal.proposal_id || busyKey) return;
    setBusyKey(`structured-${proposal.proposal_id}`);
    // Captured BEFORE the apply lands: the content Undo must restore.
    const priorContent: ResumeContent = JSON.parse(
      JSON.stringify(version.content)
    );
    try {
      const updated = await applyStructuredResumeProposal(
        version.id, proposal.proposal_id, version.contentRevision ?? 0
      );
      setVersion(updated);
      setStructuredUndo({
        proposalId: proposal.proposal_id,
        content: priorContent,
        revisionAfterApply: updated.contentRevision ?? 0,
      });
      setDirty(true);
      // Glow where the change landed (deletes of whole entries have nothing
      // left to glow — same convention as the legacy removal flow).
      await refetchPreview(updated.id, sectionIdForStructuredProposal(proposal));
      setShowSeeIt(true);
    } catch (error) {
      message.error(getApiErrorMessage(error));
      const refreshed = await getResumeVersion(version.id);
      setVersion(refreshed);
    } finally {
      setBusyKey(null);
    }
  };

  const handleStructuredUndo = async (proposal: FixProposal) => {
    if (!version || busyKey || !structuredUndo) return;
    if (structuredUndo.proposalId !== proposal.proposal_id) return;
    setBusyKey(`structured-undo-${proposal.proposal_id}`);
    try {
      // Content-only PATCH — the proposal stays 'applied' server-side (there
      // is no un-apply), mirroring how legacy undo overrides an append-only
      // decision log locally. The revision CAS still guards the write.
      const updated = await updateResumeVersion(version.id, {
        content: structuredUndo.content,
        expectedContentRevision: version.contentRevision ?? 0,
      });
      setVersion(updated);
      setStructuredUndo(null);
      setDirty(true);
      captureEvent('resume_structured_undo', {
        proposalId: proposal.proposal_id,
      });
      await refetchPreview(version.id, sectionIdForStructuredProposal(proposal));
    } catch (error) {
      message.error(getApiErrorMessage(error));
      const refreshed = await getResumeVersion(version.id);
      setVersion(refreshed);
    } finally {
      setBusyKey(null);
    }
  };

  const handleStructuredDismiss = async (proposal: FixProposal) => {
    if (!version || !proposal.proposal_id || busyKey) return;
    setBusyKey(`structured-${proposal.proposal_id}`);
    try {
      const updated = await dismissStructuredResumeProposal(
        version.id, proposal.proposal_id, version.contentRevision ?? 0
      );
      setVersion(updated);
    } catch (error) {
      message.error(getApiErrorMessage(error));
      const refreshed = await getResumeVersion(version.id);
      setVersion(refreshed);
    } finally {
      setBusyKey(null);
    }
  };

  const handleSkip = async (card: CardVM) => {
    if (!version || busyKey) return;
    if (card.kind === 'comment') {
      // Local resolve only — see CommentCardState.resolved.
      setCommentCards((prev) =>
        prev.map((c) =>
          c.key === card.key
            ? { ...c, resolved: { fix_index: -1, decision: 'skipped' } }
            : c
        )
      );
      return;
    }
    setBusyKey(card.key);
    try {
      const fixDecisions = [
        ...(version.fixContext?.decisions ?? []),
        { fix_index: card.fixIndex, decision: 'skipped' as const },
      ];
      const updated = await updateResumeVersion(version.id, {
        fixDecisions, expectedContentRevision: version.contentRevision ?? 0,
      });
      setVersion(updated);
      setUndoneFixes((prev) => {
        if (!prev.has(card.fixIndex)) return prev;
        const next = new Set(prev);
        next.delete(card.fixIndex);
        return next;
      });
    } catch (error) {
      message.error(getApiErrorMessage(error));
    } finally {
      setBusyKey(null);
    }
  };

  const handleUndo = async (card: CardVM) => {
    if (!version || busyKey || !card.decision) return;
    // The honest undo restores the pre-accept SNAPSHOT — never the card's
    // re-derived proposal, which can be null precisely because the accept
    // landed (education date drops clear the dates that made it mappable).
    // Fallback to proposal.before covers decisions persisted before this
    // page load (snapshots are session-local).
    const snapshot = undoSnapshots[card.key] ?? null;
    setBusyKey(card.key);
    setHighlightSectionId(null);
    try {
      if (card.decision.decision !== 'skipped') {
        // Content-only PATCH — no decision append (no 'undecided' exists
        // server-side; the reopen is the local override below).
        // Known edge: the snapshot (like proposal.before) is point-in-time,
        // so if the user accepted TWO proposals on the SAME section and
        // undoes the first, this revert also clobbers the second's applied
        // edit. Accepted for v1 — the preview refetch makes the outcome
        // immediately visible and re-accepting recovers.
        const restore = snapshot
          ? restoreSnapshot(version.content, snapshot)
          : card.proposal
            ? applySectionText(
                version.content,
                card.proposal.section_id,
                card.proposal.before
              )
            : null;
        if (!restore?.applied) {
          // The same contract as the accept guard: nothing was restored, so the
          // card must not reopen. Reopening it says the removal was reversed
          // while the resume still does not have the content back — the undo
          // side of the identical lie.
          //
          // Reachable in three ordinary clicks: remove a bullet, remove its
          // whole job, then Undo the BULLET card — restoreSnapshot's
          // 'delete-bullet' case cannot find the parent entry to splice into.
          // `restore === null` lands here too (no session snapshot AND no
          // re-derivable proposal — e.g. an education date drop undone after a
          // reload, where the accept is what made the proposal unmappable),
          // which is the same lie for the same reason.
          //
          // Like the accept guard, the copy names a different action: the
          // content this undo needs cannot appear without another write, so
          // clicking Undo again fails identically.
          console.error(
            `ResumeTailor: nothing to restore for card "${card.key}" — the undo was NOT applied`
          );
          captureEvent('resume_undo_failed', {
            sectionId: card.sectionId,
            op: card.op,
          });
          message.error(
            'Nothing came back — undo your newer changes first, or ask for it again.'
          );
          return;
        }
        const sectionId = snapshot?.sectionId ?? card.proposal!.section_id;
        const updated = await updateResumeVersion(version.id, {
          content: restore.content,
          expectedContentRevision: version.contentRevision ?? 0,
        });
        setVersion(updated);
        await refetchPreview(version.id, sectionId);
      }
      if (snapshot) {
        setUndoSnapshots((prev) => {
          const next = { ...prev };
          delete next[card.key];
          return next;
        });
      }
      if (card.kind === 'review') {
        setUndoneFixes((prev) => new Set(prev).add(card.fixIndex));
      } else {
        setCommentCards((prev) =>
          prev.map((c) => (c.key === card.key ? { ...c, resolved: null } : c))
        );
      }
    } catch (error) {
      message.error(getApiErrorMessage(error));
    } finally {
      setBusyKey(null);
    }
  };

  const handleSectionClick = (sectionId: string) => {
    // The contact header carries a data-section-id only so applied contact
    // edits can glow it — comments stay unwired there (no comment pipeline
    // for contact), so keep answering with the hint instead of a popover.
    if (sectionId === 'contact') {
      handleUnwiredClick();
      return;
    }
    setPopover({
      sectionId,
      label: sectionLabelFor(version?.content, sectionId),
    });
  };

  // No dead clicks: the preview answers taps on areas without a comment
  // target (only the contact header now). The `key` collapses rapid repeat
  // clicks into one visible toast instead of a stack.
  const handleUnwiredClick = () => {
    message.info({
      key: 'tailor-unwired-click',
      content:
        'Comments work on any resume section — tap below the contact header.',
    });
  };

  // The explainer hands off to the coachmark: premise first, mechanics second.
  // Esc and the mask route here too — a dismissal is a dismissal, and the
  // "Why this layout?" button keeps the answer one click away afterwards.
  const handleExplainerDismiss = () => {
    setExplainerOpen(false);
    markRebuildExplainerSeen();
    if (isCoachmarkPending()) setCoachmarkOpen(true);
  };

  const dismissCoachmark = () => {
    setCoachmarkOpen(false);
    try {
      localStorage.setItem(COACHMARK_KEY, '1');
    } catch (error) {
      // Storage unavailable — the hint simply reappears next session.
      console.warn('ResumeTailor: could not persist coachmark dismissal', error);
    }
  };

  const handleCommentSubmit = async (note: string) => {
    if (!version || !popover) return;
    const { sectionId } = popover;
    setCommentBusy(true);
    try {
      // POST returns a `processing` placeholder immediately; the poll loop
      // above fills it in by id. `note` is kept on the card for retry.
      const response = await addResumeComment(version.id, { sectionId, note });
      const key = `comment-${response.sectionId}-${commentSeq.current++}`;
      setCommentCards((prev) => [
        seedCommentCard(response, key, note),
        ...prev,
      ]);
      setFlashCardKey(key);
      pulseNonce.current += 1;
      setPreviewPulse({ sectionId, nonce: pulseNonce.current });
      captureEvent('resume_comment_added', { sectionId });
      setPopover(null);
      // On phones the click came from the preview pane — bring the new card
      // into view.
      setMobilePane('fixes');
      // Appending the card already bumped the server's CAS revision (the POST
      // returns only the card). Re-sync so Accepting ANY already-ready card in
      // the ~poll-interval gap before the fill poll first runs still echoes the
      // current revision instead of the pre-comment one (409). Done after the
      // UI updates so it never delays the popover close.
      try {
        setVersion(withContentRevision(await getResumeVersion(version.id)));
      } catch {
        // Best-effort; the fill poll reconciles the revision shortly after.
      }
    } catch (error) {
      message.error(getApiErrorMessage(error));
    } finally {
      setCommentBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!version || !popover) return;
    const { sectionId } = popover;
    setRemoveBusy(true);
    try {
      // Returns a ready `op:"delete"` card synchronously — no polling. The user
      // Accepts it to actually splice + PATCH.
      const response = await removeResumeSection(version.id, { sectionId });
      const key = `remove-${response.sectionId}-${commentSeq.current++}`;
      setCommentCards((prev) => [seedCommentCard(response, key, null), ...prev]);
      setFlashCardKey(key);
      pulseNonce.current += 1;
      setPreviewPulse({ sectionId, nonce: pulseNonce.current });
      captureEvent('resume_remove_requested', { sectionId });
      setPopover(null);
      setMobilePane('fixes');
      // The remove card is ready to Accept immediately and POST bumped the CAS
      // revision without returning it. No poll runs for a ready card, so refresh
      // once here — otherwise the Accept PATCH echoes the pre-remove revision
      // and 409s. After the UI updates so it never delays the popover close.
      try {
        setVersion(withContentRevision(await getResumeVersion(version.id)));
      } catch {
        // Best-effort; a stale revision would surface as a conflict on Accept.
      }
    } catch (error) {
      message.error(getApiErrorMessage(error));
    } finally {
      setRemoveBusy(false);
    }
  };

  /** PATCH content built by `mutate`. On 409 (a concurrent write bumped the
   *  revision) re-read, re-apply the SAME field-level edit onto the fresh
   *  content, and PATCH once more — never resend the stale payload. A second
   *  conflict surfaces to the user rather than looping.
   *
   *  Null = the edit resolved to nothing (see applyEntryEdit); the caller owns
   *  saying so, because only it knows what the user was told they were doing. */
  const patchEntryContent = async (
    mutate: (content: ResumeContent) => {
      content: ResumeContent;
      applied: boolean;
    }
  ): Promise<ResumeVersion | null> => {
    if (!version) return null;
    const first = mutate(version.content);
    if (!first.applied) return null;
    try {
      return await updateResumeVersion(version.id, {
        content: first.content,
        expectedContentRevision: version.contentRevision ?? 0,
      });
    } catch (error) {
      if (!isConflictError(error)) throw error;
      // The PATCH replaces the WHOLE content object, so re-sending `first`
      // against the new revision would erase whatever the other writer just
      // saved. Re-read and re-run the same field-level edit instead: since the
      // edit writes only the fields the form owns (see applyEntryEdit), their
      // change survives everywhere — including inside the edited entry.
      const fresh = await getResumeVersion(version.id);
      const retry = mutate(fresh.content);
      if (!retry.applied) return null;
      // No third attempt. A 409 on the retry means someone is writing right
      // now, and looping would just keep clobbering as fast as they type — the
      // error propagates to the caller's toast instead.
      return await updateResumeVersion(fresh.id, {
        content: retry.content,
        expectedContentRevision: fresh.contentRevision ?? 0,
      });
    }
  };

  const handleEntrySubmit = async (draft: EntryEditorDraft) => {
    // `baseline` is what the form was seeded with, and it is what tells an edit
    // the user made from one they left alone — see applyEntryEdit. It is never
    // null here (the modal only exists while it is set); capturing it also
    // narrows the type for the closure below.
    const baseline = entryEditor;
    if (!version || !baseline || entrySaving) return;
    const isAdd = !draft.entry.id;
    const noun = draft.kind === 'experience' ? 'job' : 'school';
    setEntrySaving(true);
    // Drop any pending highlight first so a second save on the SAME entry still
    // registers a prop change when re-armed below (mirrors handleAccept).
    setHighlightSectionId(null);
    try {
      const updated = await patchEntryContent((c) =>
        applyEntryEdit(c, draft, baseline)
      );
      if (!updated) {
        // The entry this form was opened on is no longer in the resume, so
        // there is nothing to write the typed facts onto and the PATCH would
        // have sent the content straight back unchanged. The route that
        // reaches it is the 409 retry, which re-applies onto freshly-read
        // content: another session removed the job while this form was open.
        //
        // Reporting a save here is the same lie handleAccept guards against —
        // "saved" over a resume that never changed. Bail before the close, the
        // dirty flag, the analytics event and the preview refetch; `finally`
        // still clears the busy flag, so the modal stays usable.
        //
        // The copy names a DIFFERENT action on purpose: the entry cannot come
        // back without another write, so pressing Save again fails identically.
        // Adding it fresh is the route that works, and the form is left open so
        // the typed values are still there to copy across.
        console.error(
          `ResumeTailor: no ${noun} with id "${draft.entry.id}" to save into — the entry edit was NOT applied`
        );
        // This branch records nothing else, so without the event a failure the
        // user watched happen is invisible in PostHog and Sentry.
        captureEvent('resume_entry_save_failed', {
          kind: draft.kind,
          // Always 'edit'. An add carries id '' and applyEntryEdit always
          // applies one, so it cannot reach this branch — deriving the prop
          // would dress that tautology up as a finding ("adds never fail").
          op: 'edit',
        });
        message.error(
          `This ${noun} is no longer in your resume — close this and add it back.`
        );
        return;
      }
      // The server's copy is canonical: an added entry only has an id here.
      setVersion(updated);
      setEntryEditor(null);
      setDirty(true);
      captureEvent('resume_entry_saved', {
        kind: draft.kind,
        op: isAdd ? 'add' : 'edit',
      });
      // An edit glows the entry it changed. An add glows the entry the server
      // just named — its id diffed against the pre-save list, the same move
      // the add-bullet accept makes. The glow is what carries the scroll: the
      // add lands date-ordered, possibly far off-screen, and a save the user
      // cannot see reads as "nothing happened" (2026-08-24, first prod use).
      let highlightId: string | null = draft.entry.id || null;
      if (isAdd && draft.kind === 'experience') {
        const prior = new Set(version.content.experience.map((e) => e.id));
        highlightId =
          updated.content.experience.find((e) => !prior.has(e.id))?.id ?? null;
      } else if (isAdd) {
        const prior = new Set(version.content.education.map((e) => e.id));
        highlightId =
          updated.content.education.find((e) => !prior.has(e.id))?.id ?? null;
      }
      await refetchPreview(updated.id, highlightId);
    } catch (error) {
      message.error(getApiErrorMessage(error));
    } finally {
      setEntrySaving(false);
    }
  };

  const handleRetryComment = async (card: CardVM) => {
    if (!version || busyKey) return;
    const cs = commentCards.find((c) => c.key === card.key);
    if (!cs || cs.note === null) return;
    setBusyKey(card.key);
    try {
      // Re-POST the same note; swap the failed card back to a fresh placeholder
      // (new id) so the poll loop tracks the new attempt — never the stale id.
      const response = await addResumeComment(version.id, {
        sectionId: cs.sectionId,
        note: cs.note,
      });
      setCommentCards((prev) =>
        prev.map((c) =>
          c.key === card.key
            ? {
                ...c,
                id: response.id ?? null,
                op: response.op ?? 'replace',
                status: response.status ?? 'ready',
                before: response.before,
                after: response.after,
                rationale: response.rationale,
                issue: response.issue ?? c.issue,
                failureReason: response.failureReason ?? null,
              }
            : c
        )
      );
      captureEvent('resume_comment_retried', { sectionId: cs.sectionId });
    } catch (error) {
      message.error(getApiErrorMessage(error));
    } finally {
      setBusyKey(null);
    }
  };

  const handleThemeChange = async (partial: {
    templateId?: ResumeTemplateId;
    theme?: ResumeTheme;
  }) => {
    // Theme PATCHes join the same single-flight discipline as the cards: a
    // concurrent card mutation and theme PATCH would race each other's
    // setVersion (last response wins, dropping the other's changes).
    if (!version || busyKey) return;
    setBusyKey('theme');
    try {
      const updated = await updateResumeVersion(version.id, {
        ...partial, expectedContentRevision: version.contentRevision ?? 0,
      });
      setVersion(updated);
      setDirty(true);
      // Appearance reloads never glow/scroll.
      await refetchPreview(version.id, null);
    } catch (error) {
      message.error(getApiErrorMessage(error));
    } finally {
      setBusyKey(null);
    }
  };

  const persistDraft = (mutate: (draft: NonNullable<ReturnType<typeof loadSearchDraft>>) => void) => {
    // sessionStorage can throw (quota, privacy modes); the server-side state
    // is already saved by this point, so log and keep going — the drawer
    // shape-guards and starts clean without the draft.
    try {
      const draft = loadSearchDraft();
      if (!draft) return;
      mutate(draft);
      saveSearchDraft(draft);
    } catch (error) {
      console.warn('ResumeTailor: could not persist the search draft', error);
    }
  };

  const handleApprove = async () => {
    // Never approve mid-mutation: the in-flight PATCH's result wouldn't be
    // part of what the user just approved.
    if (!version || busyKey) return;
    setApproving(true);
    try {
      await approveResumeVersion(version.id);

      // Edit round-trip: if this draft was forked from a search's tailored
      // resume, bind the approved version back to that search. Single-use +
      // version-scoped, so a stale entry from an abandoned edit can never bind
      // the wrong search.
      const bindSearchId = takePendingResumeBind(version.id);
      if (bindSearchId) {
        try {
          await bindSearchResume(bindSearchId, version.id);
        } catch (error) {
          message.error(
            `Resume approved, but applying it to the search failed: ${getApiErrorMessage(error)}`
          );
        }
      }

      persistDraft((draft) => {
        draft.resumeVersionId = version.id;
        draft.returnStep = 'plan';
      });
      captureEvent('resume_version_approved', { resolvedCount, totalFixes });
      // Edit round-trip lands on the list; the normal flow returns to the drawer.
      // On bind failure the version is approved and reusable but the search keeps its
      // old binding, and the single-use channel is already drained (no auto-retry; V1).
      navigate(bindSearchId ? '/searches' : '/searches?drawer=open');
    } catch (error) {
      message.error(getApiErrorMessage(error));
    } finally {
      setApproving(false);
    }
  };

  const leaveToSearches = () => {
    if (enteredFromSearch) {
      // Forked from an existing search's Edit/Tailor — return to the list, and
      // don't touch the create-search draft (returnStep is a wizard-only cursor).
      navigate('/searches');
      return;
    }
    persistDraft((draft) => {
      draft.returnStep = 'review';
    });
    navigate('/searches?drawer=open');
  };

  const discardDraftAndLeave = async () => {
    // Best-effort cleanup of the throwaway fork. A failed or not-yet-deployed
    // DELETE must never trap the user in the editor, so swallow and leave anyway
    // — the search never adopted the draft (approve is the only bind), so the
    // worst case is an orphan version the user has already chosen to abandon.
    if (version) {
      try {
        await deleteResumeVersion(version.id);
      } catch (error) {
        console.warn('ResumeTailor: could not discard the draft version', error);
      }
    }
    leaveToSearches();
  };

  const handleBack = () => {
    // A pristine draft has nothing to lose — leave straight away. A dirtied one
    // would strand the user's tuning (only approve binds it to the search), so
    // confirm before discarding it.
    if (!dirty) {
      leaveToSearches();
      return;
    }
    Modal.confirm({
      title: 'Discard your changes?',
      content: (
        <>
          {'Your edits to this resume aren’t saved to your search yet.'}
          <br />
          {'Leave now and they’ll be discarded.'}
        </>
      ),
      okText: 'Discard',
      okButtonProps: { danger: true },
      cancelText: 'Keep editing',
      onOk: discardDraftAndLeave,
    });
  };

  if (phase === 'loading') {
    return (
      <div className={css.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  if (phase === 'preparing') {
    // The real content is a backend placeholder right now, so the meter's
    // footprint is a skeleton and the wait becomes a coaching moment: a
    // branded loader, the (truthful, server-reported) current step, and a
    // rotating evidence-backed tip about the layout we're building.
    const stageHeading =
      PREP_STAGE_LABELS[prepStage ?? ''] ?? DEFAULT_PREP_STAGE;
    return (
      <div className={css.page}>
        <header className={css.topBar}>
          <div className={css.meterSkeleton}>
            <Skeleton active title paragraph={{ rows: 1 }} />
          </div>
        </header>
        <div className={css.loadingContainer}>
          <div className={css.prepLoader} role="status" aria-live="polite">
            <div className={css.prepMark} aria-hidden="true">
              <i />
              <i />
              <i />
            </div>
            <div className={css.prepCopy}>
              <strong className={css.prepHeading}>{stageHeading}</strong>
              <p className={css.prepSub}>This usually takes a minute or two.</p>
            </div>
          </div>
          <WaitingCoach />
        </div>
      </div>
    );
  }

  if (phase === 'prep-timeout') {
    return (
      <div className={css.loadingContainer}>
        <p className={css.loadFailed}>
          This is taking longer than usual — your draft may still be on its
          way.
        </p>
        <Button type="primary" onClick={() => {
          setPhase('preparing');
          setPollEpoch((epoch) => epoch + 1);
        }}>
          Retry
        </Button>
        <Button onClick={handleBack}>{backLabel}</Button>
      </div>
    );
  }

  if (phase === 'prep-failed') {
    return (
      <div className={css.loadingContainer}>
        <p className={css.loadFailed}>
          {prepFailureReason ??
            "We couldn't prepare this draft — please go back and try again."}
        </p>
        <Button onClick={handleBack}>{backLabel}</Button>
      </div>
    );
  }

  if (phase === 'error' || !version) {
    return (
      <div className={css.loadingContainer}>
        <p className={css.loadFailed}>
          {versionNotFound ? (
            <>
              This resume draft no longer exists. If you restarted the local
              development environment, its database may have been reset.
            </>
          ) : (
            <>We couldn't load this resume right now — please try again in a moment.</>
          )}
        </p>
        <Button onClick={handleBack}>{backLabel}</Button>
      </div>
    );
  }

  // Structural editing is DRAFT-only: the PATCH 400s on an approved version, so
  // the route is withheld rather than offered and then failed on Save.
  const entryTarget =
    version.status === 'draft' && popover
      ? entryTargetFor(version.content, popover.sectionId)
      : null;

  return (
    <div className={css.page}>
      <header className={css.topBar}>
        <ReadinessMeter
          readiness={readiness}
          resolvedCount={resolvedCount}
          totalFixes={totalFixes}
        />
        <div className={css.paneToggle}>
          <Segmented
            block
            options={[
              { label: `Fixes (${totalFixes})`, value: 'fixes' },
              { label: 'Preview', value: 'preview' },
            ]}
            value={mobilePane}
            onChange={(value) => setMobilePane(value as 'fixes' | 'preview')}
            aria-label="Editor pane"
          />
        </div>
      </header>

      <div className={css.panes}>
        <section
          className={`${css.fixesPane} ${
            mobilePane === 'fixes' ? css.activePane : ''
          }`}
          aria-label="Suggested fixes"
        >
          {batchProposalStatus === 'processing' && (
            <div className={css.proposalProgress} role="status" aria-live="polite">
              <div className={css.proposalProgressMark} aria-hidden="true">
                <i />
                <i />
                <i />
              </div>
              <div className={css.proposalProgressCopy}>
                <div className={css.proposalProgressHeading}>
                  <strong>Making suggestions actionable</strong>
                  <span>In progress</span>
                </div>
                <p>Verified changes will appear here, ready to apply.</p>
                <div className={css.proposalProgressTrack} aria-hidden="true">
                  <i />
                </div>
              </div>
            </div>
          )}
          {showSeeIt && (
            <Button
              type="link"
              className={css.seeItLink}
              onClick={() => {
                setMobilePane('preview');
                setShowSeeIt(false);
              }}
            >
              See it in your resume →
            </Button>
          )}
          <div className={css.cardList}>
            {structuredProposals.map((proposal) => (
              <StructuredProposalCard
                key={proposal.proposal_id}
                proposal={proposal}
                busy={busyKey !== null}
                onApply={() => handleStructuredApply(proposal)}
                onDismiss={() => handleStructuredDismiss(proposal)}
                undoable={
                  structuredUndo != null &&
                  structuredUndo.proposalId === proposal.proposal_id &&
                  (version?.contentRevision ?? 0) === structuredUndo.revisionAfterApply
                }
                onUndo={() => handleStructuredUndo(proposal)}
              />
            ))}
            {cards.map((card) => (
              <TailorFixCard
                key={card.key}
                fix={card.fix}
                proposal={card.proposal}
                // Hide Edit where the target isn't free text (education date
                // drops) so a typed change can't be silently discarded.
                editable={
                  card.proposal != null &&
                  !!content &&
                  isProposalTextEditable(content, card.proposal)
                }
                decision={card.decision}
                busy={busyKey !== null}
                status={card.status}
                op={card.op}
                failureReason={card.failureReason}
                // The default fallback hint routes to preview comments —
                // circular for a card the user JUST created by commenting. Say
                // something useful instead, or (structural targets, which carry
                // the server's own guidance) say nothing.
                fallbackHint={
                  card.kind === 'comment'
                    ? commentFallbackHint(
                        content,
                        card.sectionId,
                        // What the card renders as its suggestion — the server's
                        // answer, guidance or otherwise.
                        card.fix.suggestion
                      )
                    : reviewFallbackHint(content, card.sectionId)
                }
                onAccept={(text) => handleAccept(card, text)}
                onSkip={() => handleSkip(card)}
                onUndo={() => handleUndo(card)}
                onRetry={() => handleRetryComment(card)}
                flash={card.key === flashCardKey}
              />
            ))}
            {cards.length === 0 && (
              <p className={css.noFixes}>
                No fixes suggested — click any section in the preview to ask
                for a change.
              </p>
            )}
          </div>
        </section>

        <section
          className={`${css.previewPane} ${
            mobilePane === 'preview' ? css.activePane : ''
          }`}
          aria-label="Resume preview"
        >
          <div className={css.previewHeader}>
            <button
              type="button"
              className={css.whyLayout}
              onClick={() => setWhyOpen(true)}
            >
              <BulbOutlined aria-hidden="true" /> Why this layout?
            </button>
          </div>
          <div className={css.previewBox}>
            <PreviewPane
              html={previewHtml}
              highlightSectionId={highlightSectionId}
              pulse={previewPulse}
              onSectionClick={handleSectionClick}
              onUnwiredClick={handleUnwiredClick}
              loading={false}
            />
            {coachmarkOpen && (
              <div className={css.coachmark} role="note" aria-label="Editing tip">
                <span className={css.coachmarkText}>
                  Tip: click any section of your resume to suggest a change.
                </span>
                <button
                  type="button"
                  className={css.coachmarkDismiss}
                  onClick={dismissCoachmark}
                >
                  Got it
                </button>
              </div>
            )}
          </div>
          <ThemeBar
            templateId={version.templateId}
            theme={version.theme}
            busy={busyKey !== null}
            onChange={handleThemeChange}
          />
        </section>
      </div>

      <Modal
        open={whyOpen}
        onCancel={() => setWhyOpen(false)}
        footer={null}
        title="Why this layout?"
        width="min(560px, 94vw)"
      >
        <TemplateEvidence />
      </Modal>

      <Modal
        open={explainerOpen}
        onCancel={handleExplainerDismiss}
        footer={null}
        title={<RebuildExplainerTitle />}
        width="min(600px, 94vw)"
        // One-shot per user — don't leave a hidden copy in the tree.
        destroyOnClose
      >
        <RebuildExplainer variant="editor" onDismiss={handleExplainerDismiss} />
      </Modal>

      <footer className={css.footer}>
        <Button type="link" className={css.backLink} onClick={handleBack}>
          {backLabel}
        </Button>
        <Button
          type="primary"
          size="large"
          loading={approving}
          onClick={handleApprove}
        >
          Looks good — use this resume
        </Button>
      </footer>

      <CommentPopover
        open={popover !== null}
        sectionLabel={popover?.label ?? ''}
        onSubmit={handleCommentSubmit}
        onClose={() => {
          if (!commentBusy && !removeBusy) setPopover(null);
        }}
        busy={commentBusy}
        onRemove={handleRemove}
        removeLabel={removeLabelFor(version.content, popover?.sectionId ?? '')}
        removeBusy={removeBusy}
        entryActionLabel={
          entryTarget ? entryActionLabelFor(entryTarget) : undefined
        }
        onEntryAction={
          entryTarget
            ? () => {
                // One modal at a time: the comment box closes as the form opens.
                setPopover(null);
                setEntryEditor(entryTarget);
              }
            : undefined
        }
      />

      {entryEditor && (
        <EntryEditorModal
          open
          target={entryEditor}
          busy={entrySaving}
          onSubmit={handleEntrySubmit}
          onClose={() => {
            if (!entrySaving) setEntryEditor(null);
          }}
        />
      )}
    </div>
  );
};

export default ResumeTailor;
